/**
 * API DE TRADUCTION OPTIMISÉE
 * Utilise un dictionnaire massif + proxy backend pour éviter CORS
 */

import massiveTranslationService from "../services/MassiveTranslationService.js";
import apiConfig from "../config/api.js";

const getTranslateUrl = () => `${apiConfig.baseURL}/translate`;

/**
 * Détecter la langue source automatiquement
 */
const detectSourceLanguage = (text) => {
  if (!text || typeof text !== "string") return "auto";

  // Détection basique par caractères
  const hasKhmerChars = /[\u1780-\u17FF]/.test(text);
  const hasFrenchChars = /[àâäéèêëïîôöùûüÿç]/i.test(text);
  const hasAccents = /[àâäéèêëïîôöùûüÿçÀÂÄÉÈÊËÏÎÔÖÙÛÜŸÇ]/.test(text);

  if (hasKhmerChars) return "km";
  if (hasFrenchChars || hasAccents) return "fr";
  return "en";
};

/**
 * Traduction via le proxy backend (évite CORS)
 */
const translateWithBackend = async (text, targetLang, sourceLang = "auto") => {
  try {
    const response = await fetch(getTranslateUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text,
        targetLang,
        sourceLang,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    if (data?.translatedText != null) {
      return data.translatedText;
    }
    throw new Error("Invalid response format");
  } catch (error) {
    console.warn("Translation backend failed:", error.message);
    return null;
  }
};

/**
 * Cache pour les traductions
 */
const translationCache = new Map();

/**
 * API de traduction optimisée avec dictionnaire massif
 */
export const translateTextOptimized = async (
  text,
  targetLang,
  sourceLang = "auto",
) => {
  if (!text || typeof text !== "string" || text.trim().length === 0) {
    return text;
  }

  // Détecter la langue source si auto
  if (sourceLang === "auto") {
    sourceLang = detectSourceLanguage(text);
  }

  // Pas de traduction nécessaire
  if (sourceLang === targetLang) {
    return text;
  }

  // Vérifier le cache
  const cacheKey = `${text}-${sourceLang}-${targetLang}`;
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }

  let translatedText = text;

  try {
    // 1. Essayer d'abord le dictionnaire massif (ultra rapide)
    const dictTranslation = massiveTranslationService.translateWithDictionary(
      text,
      sourceLang,
      targetLang,
    );

    // Si le dictionnaire a trouvé une traduction significative
    if (dictTranslation !== text) {
      translatedText = dictTranslation;
    } else {
      // 2. Si le dictionnaire n'a pas trouvé, utiliser les APIs externes

      // Traduction via le backend (évite CORS)
      const apiTranslation = await translateWithBackend(
        text,
        targetLang,
        sourceLang,
      );

      // Utiliser la traduction de l'API si disponible
      if (apiTranslation && apiTranslation.trim().length > 0) {
        translatedText = apiTranslation;
      }
    }
  } catch (error) {
    console.error("Translation error:", error);
    // En cas d'erreur, retourner le texte original
    translatedText = text;
  }

  // Mettre en cache le résultat
  translationCache.set(cacheKey, translatedText);

  return translatedText;
};

/**
 * Traduction en lot optimisée
 */
export const translateBatchOptimized = async (
  texts,
  targetLang,
  sourceLang = "auto",
) => {
  if (!Array.isArray(texts) || texts.length === 0) {
    return texts;
  }

  // Traduire tous les textes en parallèle
  const translations = await Promise.all(
    texts.map((text) => translateTextOptimized(text, targetLang, sourceLang)),
  );

  return translations;
};

/**
 * Traduction spécialisée pour les recettes
 */
export const translateRecipeContent = async (
  content,
  targetLang,
  sourceLang = "auto",
) => {
  if (!content || typeof content !== "string") {
    return content;
  }

  // Séparer le contenu en lignes
  const lines = content.split("\n").filter((line) => line.trim().length > 0);

  if (lines.length === 0) {
    return content;
  }

  // Traduire chaque ligne
  const translatedLines = await translateBatchOptimized(
    lines,
    targetLang,
    sourceLang,
  );

  // Reconstituer le contenu
  return translatedLines.join("\n");
};

/**
 * Vider le cache de traduction
 */
export const clearTranslationCache = () => {
  translationCache.clear();
  massiveTranslationService.clearCache();
};

/**
 * Obtenir les statistiques du cache
 */
export const getTranslationCacheStats = () => {
  return {
    cacheSize: translationCache.size,
    dictionaryCacheSize: massiveTranslationService.getCacheSize(),
    totalCacheSize:
      translationCache.size + massiveTranslationService.getCacheSize(),
  };
};

/**
 * Optimiser les performances de traduction
 */
export const optimizeTranslationPerformance = () => {
  // Précharger les traductions courantes
  const commonWords = [
    "ingredients",
    "instructions",
    "preparation",
    "cooking",
    "servings",
    "minutes",
    "hours",
    "easy",
    "medium",
    "hard",
    "salt",
    "pepper",
    "oil",
    "butter",
    "flour",
    "sugar",
    "cut",
    "chop",
    "mix",
    "cook",
    "heat",
    "boil",
  ];

  // Précharger pour les langues supportées
  const languages = ["fr", "km"];
  commonWords.forEach((word) => {
    languages.forEach((lang) => {
      translateTextOptimized(word, lang, "en");
    });
  });
};

// Optimiser les performances au démarrage
optimizeTranslationPerformance();

export default {
  translateTextOptimized,
  translateBatchOptimized,
  translateRecipeContent,
  clearTranslationCache,
  getTranslationCacheStats,
  optimizeTranslationPerformance,
};
